import { isCancel } from 'axios';
import { DateTime } from 'luxon';
import { z } from 'zod';

import {
  codeBasedLookupSchema,
  idBasedLookupSchema,
  slugBasedLookupSchema
} from '@/types/lookup';

//export const meetingFormatSchema = ['in_person', 'virtual', 'hybrid'] as const;
export const meetingFormatSchema = ['InPerson', 'Virtual', 'Hybrid'] as const;

export type MeetingFormat = typeof meetingFormatSchema[number];

export const meetingLocationAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().nullish(),
  city: z.string(),
  state: z.string(),
  zipcode: z.string(),
});

export const meetingWebinarSchema = z.object({
  url: z.string().nullish(),
  phone: z.string().nullish(),
  meetingId: z.string().nullish(),
  password: z.string().nullish(),
});

export const meetingLocationSchema = z.object({
  venue: z.string().nullish(),
  room: z.string().nullish(),
  address: meetingLocationAddressSchema.nullish(),
});

const meetingResourcesSchema = z.object({
  agendaLink: z.string().nullish(),
  meetingMinutesLink: z.string().nullish(),
  recordingLink: z.string().nullish(),
  speakerPoliciesLink: z.string().nullish(),
  statePdlLink: z.string().nullish(),
});

const isTrackedDrugSchema = slugBasedLookupSchema.extend({
  isTracked: z.boolean()
});

const isTrackedClassificationSchema = idBasedLookupSchema.extend({
  isTracked: z.boolean()
});

export const meetingSchema = z.object({
  id: z.number(),
  state: codeBasedLookupSchema,
  pool: z.string().nullish(),
  startTime: z.string(),
  endTime: z.string().nullish(),
  timezone: z.string(),
  format: z.enum(meetingFormatSchema),
  isCanceled: z.boolean(),
  locationNotes: z.string().nullish(),
  type: codeBasedLookupSchema,
  drugs: isTrackedDrugSchema.array(),
  classifications: isTrackedClassificationSchema.array(),
  boardMembers: z.string().nullish(),
  location: meetingLocationSchema.nullish(),
  webinar: meetingWebinarSchema.nullish(),
  resources: meetingResourcesSchema.nullish()
});

export type Meeting = z.infer<typeof meetingSchema>;

export const formatMeetingFormat = (format: MeetingFormat) => {
  switch (format) {
    case 'InPerson':
      return 'In-Person';
    case 'Virtual':
      return 'Virtual';
    case 'Hybrid':
      return 'Hybrid';
  }
};

export const parseMeetingDateTime = (meeting: Meeting) => {
  const startTime = DateTime.fromISO(meeting.startTime);
  const endTime = meeting.endTime ? DateTime.fromISO(meeting.endTime) : null;

  const localStartTime = startTime.setZone(meeting.timezone);
  const localEndTime = endTime?.setZone(meeting.timezone);

  return {
    date: localStartTime.toFormat('DDD'),
    startTime: localStartTime.toFormat('h:mm a ZZZZ'),
    endTime: localEndTime?.toFormat('h:mm a ZZZZ')
  };
};
